import { forwardRef, Ref, ReactElement, ReactNode } from "react";
import Select, {
  Props,
  SelectInstance as ReactSelectInstance,
  components as selectComponents,
  DropdownIndicatorProps,
  OptionProps,
  SingleValueProps,
  MenuProps,
  ControlProps,
  ClearIndicatorProps,
} from "react-select";
import makeAnimated from "react-select/animated";
import Image from "next/image";
import IconSprite from "@/components/UI/IconSprite";

// Utils
import st from "./index.module.scss";

export type ProviderSelectItemType = {
  value: string;
  label: string;
  count: number;
  image: string;
};

const components = {
  ...selectComponents,
  ...makeAnimated(),
};

const Control = ({ children, ...restProps }: ControlProps<ProviderSelectItemType>) => {
  return (
    <components.Control {...restProps} className={restProps.hasValue ? "select-has-value" : ""}>
      <div className={st["control-icon"]}>
        <IconSprite name={restProps.hasValue ? "filter-check" : "filter"} />
      </div>
      {children}
    </components.Control>
  );
};

const ClearIndicator = (props: ClearIndicatorProps<ProviderSelectItemType>) => {
  const { selectProps, hasValue } = props;
  if (hasValue && selectProps.menuIsOpen) return null;
  return (
    <components.ClearIndicator {...props}>
      <IconSprite name="close" />
    </components.ClearIndicator>
  );
};

type SelectDropdownIndicatorProps = DropdownIndicatorProps<ProviderSelectItemType, false>;
const DropdownIndicator = (props: SelectDropdownIndicatorProps) => {
  const { selectProps, hasValue } = props;
  if (hasValue && !selectProps.menuIsOpen) return null;
  return (
    <components.DropdownIndicator {...props}>
      {selectProps.menuIsOpen ? (
        <IconSprite name="arrow-1-up" />
      ) : (
        <IconSprite name="arrow-1-down" />
      )}
    </components.DropdownIndicator>
  );
};

type SelectMenuProps = MenuProps<ProviderSelectItemType> & {
  menuFooter?: ReactNode;
};
const Menu = ({ children, menuFooter, ...restProps }: SelectMenuProps) => {
  return (
    <components.Menu {...restProps}>
      {children}
      {menuFooter}
    </components.Menu>
  );
};

type SelectSingleValueProps = SingleValueProps<ProviderSelectItemType> & {
  placeholder: ReactNode;
};

const SingleValue = ({ placeholder, ...restProps }: SelectSingleValueProps) => {
  return (
    <components.SingleValue {...restProps} className={st["select-item"]}>
      <div className={st["value-text"]}>
        <span className={st["value-type"]}>{placeholder}</span>
        <span className={st["value-name"]}>{restProps.data.label}</span>
      </div>
    </components.SingleValue>
  );
};
const Option = (props: OptionProps<ProviderSelectItemType>) => {
  const { image, label, count } = props.data;
  const JSX = (
    <>
      <div className={st["option-left"]}>
        <div className={st["option-pick"]}>
          <Image
            className={st["option-pick-img"]}
            src={image}
            alt={label}
            width={24}
            height={24}
            loading="lazy"
            quality={100}
          />
        </div>
        <div className={st["option-text"]}>{label}</div>
      </div>
      <div className={st["option-count"]}>{count}</div>
    </>
  );

  return <components.Option {...props}>{JSX}</components.Option>;
};

export type SelectProviderProps = Props<ProviderSelectItemType> & {
  loading?: boolean;
  providerReset?: React.FC;
};

export const SelectProvider = forwardRef(
  (
    { providerReset, loading, placeholder, ...selectProps }: SelectProviderProps,
    ref: Ref<ReactSelectInstance<ProviderSelectItemType>>,
  ): ReactElement => {
    return (
      <Select
        {...selectProps}
        className={st.select}
        classNamePrefix="react-select"
        ref={ref}
        isDisabled={loading}
        instanceId={selectProps.name}
        placeholder={placeholder}
        components={{
          ...components,
          Control,
          Menu: ({ children, ...restProps }) => {
            const { clearValue, hasValue } = restProps;
            const menuFooter = !hasValue
              ? null
              : providerReset?.({
                  onReset: clearValue,
                });
            return (
              <Menu menuFooter={menuFooter} {...restProps}>
                {children}
              </Menu>
            );
          },
          DropdownIndicator,
          ClearIndicator,
          SingleValue: ({ children, ...restProps }) => {
            return (
              <SingleValue placeholder={placeholder} {...restProps}>
                {children}
              </SingleValue>
            );
          },
          Option,
          IndicatorSeparator: () => null,
        }}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            flexWrap: "nowrap",
            background: "none",
            zIndex: 3,
            height: "48px",
            cursor: "pointer",
            border: "2px solid var(--gray-500-p-40)",
            boxShadow: "none",
            borderRadius: "16px",
            marginRight: "0",
            marginLeft: "auto",
            padding: "12px",
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            background: "none",
            zIndex: 50,
          }),
          menuList: (baseStyles) => ({
            ...baseStyles,
            maxHeight: "318px",
            paddingTop: "0",
            paddingBottom: "0",
          }),
          option: (baseStyles) => ({
            ...baseStyles,
            background: "none",
            cursor: "pointer",
          }),
        }}
      />
    );
  },
);
SelectProvider.displayName = "SelectProvider";
